import { useState } from "react";
import { Link } from "react-router";
import {
  User,
  Settings,
  LogOut,
  AlertTriangle,
  ChevronRight,
} from "lucide-react";
import { AdminSpaceLink } from "@/features/auth/components/AdminSpaceLink";
import {
  USER_PROFILE_PROFIL_TAB,
  USER_PROFILE_AUTORISATIONS_TAB,
} from "@/features/userProfile/routes";
import { useUser } from "@/shared/hooks/useUser";
import { useUserMandatoryFieldsCheck } from "@/shared/hooks/useUserMandatoryFieldsCheck";
import { cn } from "@/shared/utils/style.util";

function MobileUserProfil() {
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const user = useUser();
  const { hasMandatoryMissingFields, isLoading: isMandatoryFieldsLoading } =
    useUserMandatoryFieldsCheck();

  const handleAccordionToggle = () => {
    setIsAccordionOpen((prev) => !prev);
  };

  const handleAccordionKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleAccordionToggle();
    }
  };

  return (
    <div className="rounded-lg bg-sidebar-accent dark:bg-gray-700 p-4">
      <button
        type="button"
        onClick={handleAccordionToggle}
        onKeyDown={handleAccordionKeyDown}
        aria-expanded={isAccordionOpen}
        aria-label={isAccordionOpen ? "Fermer le menu profil" : "Ouvrir le menu profil"}
        className="flex w-full items-center gap-3 min-w-0 text-left"
      >
        {user.avatar
          ? (
            <img
              className="size-6 rounded-full object-cover flex-shrink-0"
              src={
                typeof user.avatar === "string" ? user.avatar : user.avatar?.url
              }
              alt=""
            />
          )
          : (
            <div className="size-6 rounded-full bg-gray-200 text-primary flex items-center justify-center flex-shrink-0 text-xs">
              {user.initials}
            </div>
          )}
        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-xs font-semibold text-sidebar-foreground truncate">{`${user.first_name} ${user.last_name}`}</span>
          <span className="text-xs text-sidebar-foreground/70 truncate">
            {user.email}
          </span>
        </div>
        <ChevronRight
          size={20}
          className={cn(
            "flex-shrink-0 text-sidebar-foreground transition-transform duration-200",
            isAccordionOpen && "rotate-90",
          )}
          aria-hidden
        />
      </button>
      {isAccordionOpen && (
        <div className="flex flex-col gap-1 space-y-2 mt-3">
          <Link
            className="flex items-center text-xs text-sidebar-foreground/80 hover:text-sidebar-primary transition-colors duration-100"
            to={USER_PROFILE_PROFIL_TAB}
          >
            <User className="mr-2 shrink-0" size={20} />
            Mon profil
          </Link>
          <Link
            className={cn(
              "flex items-center text-xs transition-colors duration-100",
              hasMandatoryMissingFields && !isMandatoryFieldsLoading
                ? "text-red-600 dark:text-red-500 hover:text-red-700 dark:hover:text-red-400"
                : "text-sidebar-foreground/80 hover:text-sidebar-primary",
            )}
            to={USER_PROFILE_AUTORISATIONS_TAB}
          >
            <Settings className="mr-2 shrink-0" size={20} />
            <span className="flex-1">Paramètres du compte</span>
            {hasMandatoryMissingFields && !isMandatoryFieldsLoading && (
              <AlertTriangle className="ml-auto flex-shrink-0" size={20} />
            )}
          </Link>
          {user.linked_representative_id && (
            <AdminSpaceLink className="flex items-center text-xs text-sidebar-foreground/80 hover:text-sidebar-primary transition-colors duration-100" />
          )}
          <Link
            className="flex items-center text-xs text-sidebar-foreground/80 hover:text-sidebar-primary transition-colors duration-100"
            to="/deconnexion"
          >
            <LogOut className="mr-2 shrink-0" size={20} />
            Se déconnecter
          </Link>
        </div>
      )}
    </div>
  );
}

export default MobileUserProfil;
